import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast, Toaster } from "sonner";
import {
  Users, UserPlus, KeyRound, Ban, Pencil, GitBranch, Loader2, Search,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const NO_MANAGER = "__none__";

const ADMIN_ROLES = [
  "super_admin", "claims_officer", "compliance_officer", "underwriter",
  "finance_officer", "fraud_analyst", "support_agent",
];

function RolePill({ role }) {
  const map = {
    super_admin: "bg-[#c15337]/12 text-[#8b3a23] border-[#c15337]/25",
    claims_officer: "bg-[#114232]/8 text-[#114232] border-[#114232]/15",
    compliance_officer: "bg-[#d49a36]/15 text-[#8a5a16] border-[#d49a36]/30",
    finance_officer: "bg-[#114232]/8 text-[#114232] border-[#114232]/15",
    underwriter: "bg-stone-100 text-stone-700 border-stone-200",
    support_agent: "bg-stone-100 text-stone-700 border-stone-200",
    fraud_analyst: "bg-[#c15337]/12 text-[#8b3a23] border-[#c15337]/25",
    merchant: "bg-[#114232]/6 text-[#114232] border-[#114232]/15",
    merchant_staff: "bg-[#114232]/6 text-[#114232] border-[#114232]/12",
    partner_admin: "bg-[#d49a36]/15 text-[#8a5a16] border-[#d49a36]/30",
    partner_analyst: "bg-[#d49a36]/12 text-[#8a5a16] border-[#d49a36]/25",
    partner_viewer: "bg-stone-100 text-stone-700 border-stone-200",
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold border ${map[role] || "bg-stone-100 text-stone-700 border-stone-200"}`}>
      {(role || "—").replace(/_/g, " ")}
    </span>
  );
}

function StatusPill({ status }) {
  const isDisabled = status === "disabled";
  return (
    <span
      data-testid={`status-pill-${isDisabled ? "disabled" : "active"}`}
      className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold border ${
        isDisabled
          ? "bg-[#c15337]/12 text-[#8b3a23] border-[#c15337]/25"
          : "bg-[#114232]/8 text-[#114232] border-[#114232]/15"
      }`}
    >
      {isDisabled ? "Disabled" : "Active"}
    </span>
  );
}

function CreateUserModal({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({
    email: "", name: "", password: "", role: "support_agent",
  });
  const [saving, setSaving] = useState(false);

  function update(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/auth/users", form);
      toast.success("User created");
      setForm({ email: "", name: "", password: "", role: "support_agent" });
      onCreated();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="create-user-dialog">
        <DialogHeader>
          <DialogTitle>Create admin user</DialogTitle>
          <DialogDescription>
            New users receive an immediate login. Use sandbox / strong passwords.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="create-user-form">
          <div>
            <Label htmlFor="cu-email">Email</Label>
            <Input id="cu-email" type="email" required data-testid="create-user-email"
              value={form.email} onChange={(e) => update("email", e.target.value)} />
          </div>
          <div>
            <Label htmlFor="cu-name">Full name</Label>
            <Input id="cu-name" required minLength={2} data-testid="create-user-name"
              value={form.name} onChange={(e) => update("name", e.target.value)} />
          </div>
          <div>
            <Label htmlFor="cu-pwd">Password (min 8)</Label>
            <Input id="cu-pwd" type="password" required minLength={8} data-testid="create-user-password"
              value={form.password} onChange={(e) => update("password", e.target.value)} />
          </div>
          <div>
            <Label htmlFor="cu-role">Role</Label>
            <Select value={form.role} onValueChange={(v) => update("role", v)}>
              <SelectTrigger id="cu-role" data-testid="create-user-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ADMIN_ROLES.map((r) => (
                  <SelectItem key={r} value={r}>{r.replace(/_/g, " ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="create-user-submit">
              {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Create user
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditUserModal({ target, allUsers, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [managerId, setManagerId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (target) {
      setName(target.name || "");
      setRole(target.role || "");
      setManagerId(target.manager_id || "");
    }
  }, [target]);

  if (!target) return null;

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {};
      if (name && name !== target.name) body.name = name;
      if (role && role !== target.role) body.role = role;
      if (managerId !== (target.manager_id || "")) body.manager_id = managerId;
      if (Object.keys(body).length === 0) {
        onClose(); return;
      }
      await api.patch(`/admin/users/${target.id}`, body);
      toast.success("User updated");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-user-dialog">
        <DialogHeader>
          <DialogTitle>Edit user · {target.email}</DialogTitle>
          <DialogDescription>Update name, role or reporting line.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="edit-user-form">
          <div>
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-user-name" />
          </div>
          <div>
            <Label>Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="edit-user-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ADMIN_ROLES.map((r) => (
                  <SelectItem key={r} value={r}>{r.replace(/_/g, " ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Manager (reporting line)</Label>
            <Select
              value={managerId || NO_MANAGER}
              onValueChange={(v) => setManagerId(v === NO_MANAGER ? "" : v)}
            >
              <SelectTrigger data-testid="edit-user-manager">
                <SelectValue placeholder="— None —" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_MANAGER}>— None —</SelectItem>
                {allUsers.filter((u) => u.id !== target.id && ADMIN_ROLES.includes(u.role))
                  .map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name} ({u.email})
                    </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="edit-user-submit">
              {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Save changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordModal({ target, onClose }) {
  const [pwd, setPwd] = useState("");
  const [saving, setSaving] = useState(false);
  if (!target) return null;

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/admin/users/${target.id}/reset-password`, { new_password: pwd });
      toast.success(`Password reset for ${target.email}`);
      setPwd("");
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="reset-password-dialog">
        <DialogHeader>
          <DialogTitle>Reset password · {target.email}</DialogTitle>
          <DialogDescription>The user will be able to log in immediately with the new password.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Label>New password (min 8 chars)</Label>
          <Input type="password" required minLength={8} value={pwd}
            data-testid="reset-password-input"
            onChange={(e) => setPwd(e.target.value)} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="reset-password-submit">
              {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Reset password
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ReportsDrawer({ target, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!target) { setData(null); return; }
    api.get(`/admin/users/${target.id}/reports`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [target]);

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="reports-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitBranch className="w-4 h-4 text-[#c15337]" /> Direct reports
          </DialogTitle>
          <DialogDescription>
            {data?.manager ? `${data.manager.name} · ${data.manager.email}` : "Loading…"}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="reports-list">
          {!data && <Loader2 className="w-5 h-5 animate-spin text-[#114232]" />}
          {data && data.direct_reports.length === 0 && (
            <div className="text-sm text-[#5c6360] py-4 text-center" data-testid="reports-empty">
              No direct reports yet. Assign one via the Edit button.
            </div>
          )}
          {data?.direct_reports.map((r) => (
            <div key={r.id} className="border border-[#114232]/10 rounded p-2.5 flex justify-between gap-2 items-center"
                  data-testid={`report-row-${r.id}`}>
              <div>
                <div className="text-sm font-medium text-[#114232]">{r.name}</div>
                <div className="text-xs text-[#5c6360] font-mono">{r.email}</div>
              </div>
              <RolePill role={r.role} />
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [resetPwd, setResetPwd] = useState(null);
  const [reportsOf, setReportsOf] = useState(null);
  const [disableTarget, setDisableTarget] = useState(null);
  const [filters, setFilters] = useState({ role: "", status: "", q: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.role) params.set("role", filters.role);
      if (filters.status) params.set("status", filters.status);
      if (filters.q) params.set("q", filters.q);
      params.set("limit", "200");
      const [r, rAll] = await Promise.all([
        api.get(`/admin/users?${params.toString()}`),
        api.get("/admin/users?limit=500"),
      ]);
      setUsers(r.data.items || []);
      setAllUsers(rAll.data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  async function confirmDisable() {
    if (!disableTarget) return;
    try {
      await api.delete(`/admin/users/${disableTarget.id}`);
      toast.success(`Disabled ${disableTarget.email}`);
      setDisableTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  return (
    <div className="p-6 space-y-6" data-testid="admin-users-page">
      <Toaster position="top-right" />
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2.5">
          <Users className="w-6 h-6 text-[#c15337]" />
          <div>
            <h1 className="font-heading text-2xl font-bold text-[#114232]">Users & hierarchy</h1>
            <div className="text-xs text-[#5c6360]">
              Manage admin staff, merchant owners, partner-bank teams and reporting lines.
            </div>
          </div>
        </div>
        <Button
          data-testid="open-create-user"
          onClick={() => setCreateOpen(true)}
          className="bg-[#c15337] hover:bg-[#a3432a]"
        >
          <UserPlus className="w-4 h-4 mr-1.5" /> New admin user
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#114232]/10 rounded-md p-3 flex flex-wrap gap-3 items-end"
            data-testid="users-filters">
        <div className="flex-1 min-w-[180px]">
          <Label className="text-[10px] uppercase tracking-wider text-[#5c6360]">Search</Label>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-stone-400" />
            <Input
              data-testid="users-search"
              className="pl-8"
              placeholder="email or name"
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            />
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-[#5c6360]">Role</Label>
          <Select
            value={filters.role || "__all__"}
            onValueChange={(v) => setFilters({ ...filters, role: v === "__all__" ? "" : v })}
          >
            <SelectTrigger data-testid="users-filter-role" className="min-w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All roles</SelectItem>
              {[...ADMIN_ROLES, "merchant", "merchant_staff", "partner_admin",
                "partner_analyst", "partner_viewer"].map((r) => (
                  <SelectItem key={r} value={r}>{r.replace(/_/g, " ")}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-[#5c6360]">Status</Label>
          <Select
            value={filters.status || "__all__"}
            onValueChange={(v) => setFilters({ ...filters, status: v === "__all__" ? "" : v })}
          >
            <SelectTrigger data-testid="users-filter-status" className="min-w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="disabled">Disabled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Users table */}
      <div className="bg-white border border-[#114232]/10 rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="text-[11px] uppercase tracking-wider text-stone-500">
            <tr>
              <th className="text-left font-medium py-3 px-5">User</th>
              <th className="text-left font-medium py-3 px-5">Role</th>
              <th className="text-left font-medium py-3 px-5">Status</th>
              <th className="text-left font-medium py-3 px-5">Scope</th>
              <th className="text-left font-medium py-3 px-5">Reports to</th>
              <th className="text-right font-medium py-3 px-5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="py-10 text-center">
                <Loader2 className="w-5 h-5 animate-spin inline text-[#114232]" />
              </td></tr>
            )}
            {!loading && users.length === 0 && (
              <tr><td colSpan={6} className="py-10 text-center text-[#5c6360]" data-testid="users-empty">
                No users match those filters.
              </td></tr>
            )}
            {!loading && users.map((u) => {
              const manager = allUsers.find((m) => m.id === u.manager_id);
              return (
                <tr key={u.id} className="border-t border-[#114232]/10" data-testid={`user-row-${u.id}`}>
                  <td className="py-3 px-5">
                    <div className="font-medium text-[#114232]">{u.name}</div>
                    <div className="text-xs text-[#5c6360] font-mono">{u.email}</div>
                  </td>
                  <td className="py-3 px-5"><RolePill role={u.role} /></td>
                  <td className="py-3 px-5"><StatusPill status={u.status} /></td>
                  <td className="py-3 px-5 text-xs font-mono text-[#5c6360]">
                    {u.merchant_id ? `M·${u.merchant_id.slice(0, 8)}` :
                      u.partner_id ? `P·${u.partner_id.slice(0, 8)}` : "—"}
                  </td>
                  <td className="py-3 px-5 text-xs text-[#5c6360]">
                    {manager ? manager.name : "—"}
                  </td>
                  <td className="py-3 px-5">
                    <div className="flex items-center justify-end gap-1.5">
                      <Button variant="ghost" size="sm" onClick={() => setReportsOf(u)}
                                data-testid={`view-reports-${u.id}`} title="View reports">
                        <GitBranch className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setEdit(u)}
                                data-testid={`edit-user-${u.id}`} title="Edit user">
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setResetPwd(u)}
                                data-testid={`reset-pwd-${u.id}`} title="Reset password">
                        <KeyRound className="w-3.5 h-3.5" />
                      </Button>
                      {u.status !== "disabled" && (
                        <Button variant="ghost" size="sm" onClick={() => setDisableTarget(u)}
                                  data-testid={`disable-user-${u.id}`} title="Disable user"
                                  className="text-[#c15337] hover:bg-[#c15337]/10">
                          <Ban className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <CreateUserModal open={createOpen} onOpenChange={setCreateOpen} onCreated={load} />
      <EditUserModal target={edit} allUsers={allUsers} onClose={() => setEdit(null)} onSaved={load} />
      <ResetPasswordModal target={resetPwd} onClose={() => setResetPwd(null)} />
      <ReportsDrawer target={reportsOf} onClose={() => setReportsOf(null)} />

      <AlertDialog open={!!disableTarget} onOpenChange={(o) => !o && setDisableTarget(null)}>
        <AlertDialogContent data-testid="disable-user-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Disable this user?</AlertDialogTitle>
            <AlertDialogDescription>
              {disableTarget?.email} will be unable to log in until re-enabled.
              Audit records remain intact.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="disable-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="disable-confirm"
              onClick={confirmDisable}
              className="bg-[#c15337] hover:bg-[#a3432a]"
            >Disable</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
